import * as React from "react"
import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

const pageTitleVariants = cva(
  "mb-5 font-montserrat text-[0.9375rem] font-bold text-gray-900 uppercase",
  {
    variants: {
      align: {
        left: "text-left",
        center: "text-center",
      },
    },
    defaultVariants: {
      align: "left",
    },
  }
)

export interface PageTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof pageTitleVariants> {
  children: React.ReactNode
  as?: 'h1' | 'h2' | 'h3'
}

/**
 * PageTitle Component
 *
 * Reusable page title component with consistent typography across the webapp.
 * Uses CVA for alignment variants and supports semantic heading levels.
 *
 * @example
 * ```tsx
 * <PageTitle>My Collection</PageTitle>
 * <PageTitle align="center">My Profile</PageTitle>
 * <PageTitle as="h2">Section Title</PageTitle>
 * ```
 */
export const PageTitle = React.forwardRef<HTMLHeadingElement, PageTitleProps>(
  ({ className, children, as: Component = 'h1', align, style, ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(pageTitleVariants({ align }), className)}
        style={{ lineHeight: "normal", ...style }}
        {...props}
      >
        {children}
      </Component>
    )
  }
)

PageTitle.displayName = "PageTitle"
